import React from 'react';
import { Box, Typography, Chip, Grid, Paper } from '@mui/material';
import {
  Lock,
  Shield,
  Database as Storage,
  KeyRound as VpnKey,
  UserCheck as VerifiedUser,
  ShieldCheck as Security,
  CloudCheck as CloudDone,
  Bug as BugReport
} from 'lucide-react';
import PolicyLayout from './PolicyLayout';

const SecurityFeature = ({ icon, title, description }) => (
  <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ color: '#1976d2', mt: 0.5 }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#4a5568' }}>
          {description}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const SecurityPage = () => {
  const securityFeatures = [
    {
      icon: <Lock size={24} />,
      title: 'Encryption at Rest',
      description: 'All data stored in our systems is encrypted using AES-256 encryption, ensuring your information remains protected even at rest.'
    },
    {
      icon: <VpnKey size={24} />,
      title: 'Encryption in Transit',
      description: 'All data transmitted to and from AppealBot uses TLS 1.2 or higher, preventing interception and tampering.'
    },
    {
      icon: <Shield size={24} />,
      title: 'Access Controls',
      description: 'Role-based access controls ensure that only authorized personnel can access sensitive data, with the principle of least privilege applied.'
    },
    {
      icon: <VerifiedUser size={24} />,
      title: 'Authentication',
      description: 'Secure authentication with optional multi-factor authentication (MFA) to protect your account from unauthorized access.'
    },
    {
      icon: <Storage size={24} />,
      title: 'Secure Infrastructure',
      description: 'Hosted on enterprise-grade cloud infrastructure with SOC 2 certified data centers, redundant systems, and disaster recovery.'
    },
    {
      icon: <CloudDone size={24} />,
      title: 'Regular Backups',
      description: 'Automated encrypted backups ensure your data is protected against loss and can be recovered in case of any incident.'
    },
    {
      icon: <BugReport size={24} />,
      title: 'Vulnerability Management',
      description: 'Regular security assessments, penetration testing, and continuous monitoring to identify and address vulnerabilities.'
    },
    {
      icon: <Security size={24} />,
      title: 'Incident Response',
      description: 'Comprehensive incident response procedures to quickly detect, respond to, and recover from security events.'
    }
  ];

  return (
    <PolicyLayout title="Security" lastUpdated="January 20, 2026">
      <p>
        At AppealBot, security is foundational to everything we do. As a healthcare technology
        platform handling Protected Health Information (PHI), we implement comprehensive security
        measures to protect your data.
      </p>

      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label="HIPAA Compliant" color="primary" variant="outlined" />
          <Chip label="Encrypted" color="primary" variant="outlined" />
          <Chip label="SOC 2 Aligned" color="primary" variant="outlined" />
        </Box>
      </Box>

      <h2>Security Features</h2>
      <Grid container spacing={3} sx={{ my: 2 }}>
        {securityFeatures.map((feature, index) => (
          <Grid item xs={12} md={6} key={index}>
            <SecurityFeature {...feature} />
          </Grid>
        ))}
      </Grid>

      <h2>Data Protection</h2>

      <h3>Physical Security</h3>
      <ul>
        <li>Data centers with 24/7 physical security and access controls</li>
        <li>Biometric authentication for data center access</li>
        <li>Video surveillance and intrusion detection systems</li>
        <li>Environmental controls (fire suppression, climate control)</li>
      </ul>

      <h3>Network Security</h3>
      <ul>
        <li>Web Application Firewall (WAF) protection</li>
        <li>DDoS mitigation and prevention</li>
        <li>Intrusion detection and prevention systems (IDS/IPS)</li>
        <li>Network segmentation and isolation</li>
        <li>Regular network security assessments</li>
      </ul>

      <h3>Application Security</h3>
      <ul>
        <li>Secure software development lifecycle (SDLC)</li>
        <li>Regular code reviews and security testing</li>
        <li>Automated vulnerability scanning</li>
        <li>Third-party penetration testing</li>
        <li>Dependency monitoring and updates</li>
      </ul>

      <h2>Compliance</h2>
      <p>
        AppealBot maintains compliance with healthcare and data protection regulations:
      </p>
      <ul>
        <li><strong>HIPAA:</strong> Full compliance with Privacy and Security Rules for PHI protection</li>
        <li><strong>SOC 2:</strong> Controls aligned with Trust Services Criteria</li>
        <li><strong>State Privacy Laws:</strong> Compliance with applicable state regulations</li>
      </ul>

      <h2>Security Practices</h2>

      <h3>Employee Security</h3>
      <ul>
        <li>Background checks for all employees</li>
        <li>Regular security awareness training</li>
        <li>HIPAA compliance training</li>
        <li>Strict access controls based on job function</li>
        <li>Secure workstation policies</li>
      </ul>

      <h3>Vendor Management</h3>
      <ul>
        <li>Security assessments for all vendors</li>
        <li>Business Associate Agreements where required</li>
        <li>Regular vendor security reviews</li>
        <li>Contractual security requirements</li>
      </ul>

      <h2>Incident Response</h2>
      <p>
        Our incident response program includes:
      </p>
      <ul>
        <li>24/7 security monitoring and alerting</li>
        <li>Documented incident response procedures</li>
        <li>Breach notification within 24 hours of discovery</li>
        <li>Post-incident analysis and remediation</li>
        <li>Regular incident response drills</li>
      </ul>

      <h2>Responsible Disclosure</h2>
      <p>
        We value the security research community. If you discover a security vulnerability,
        please report it responsibly:
      </p>
      <ul>
        <li>Email: security@appealbot.com</li>
        <li>Please provide detailed information about the vulnerability</li>
        <li>Allow reasonable time for us to address the issue</li>
        <li>Do not access or modify data belonging to others</li>
      </ul>

      <h2>Contact</h2>
      <p>
        For security questions or to report a concern:
      </p>
      <ul>
        <li>Security Team: security@appealbot.com</li>
        <li>Privacy Officer: privacy@appealbot.com</li>
      </ul>
    </PolicyLayout>
  );
};

export default SecurityPage;
